import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Spin } from 'antd';
import {
  CreateClinicLocationManagerForm,
  massagerSerialForManagerEditBreadcrumb,
} from '@/pages/admin/admin-clinic-locations/CreateClinicLocationManagerPage';
import {
  useClinicLocationManagerQuery,
  useClinicLocationQuery,
  useDeleteClinicLocationManagerMutation,
  useUpdateClinicLocationManagerMutation,
  parseClinicLocationId,
} from '@/hooks/admin/useClinicLocationCreate';
import SiteAdminNotFoundTemplate from '@/components/templates/SiteAdminNotFoundTemplate';
import { isNotFoundError } from '@/utils/apiError';
import { useSiteAdminAuth } from '@/shared/providers/SiteAdminAuthContext';
import type {
  ClinicLocationManagerUpdatePayload,
  ClinicLocationMassagerItem,
} from '@/services/admin/clinic-locations';
import { AuditInfo } from '@/components/molecules';

export default function EditClinicLocationManagerPage() {
  const navigate = useNavigate();
  const { site, id, managerId } = useParams<{ site: string; id: string; managerId: string }>();
  const { currentSite } = useSiteAdminAuth();

  const siteType = site ?? currentSite ?? 'medix';
  const locationId = parseClinicLocationId(id ?? null);
  const managerIdNum = managerId ? parseInt(managerId, 10) : null;

  const locationQuery = useClinicLocationQuery(siteType, id ?? null);
  const managerQuery = useClinicLocationManagerQuery(
    siteType,
    locationId ?? null,
    managerIdNum
  );

  const updateMutation = useUpdateClinicLocationManagerMutation();
  const deleteMutation = useDeleteClinicLocationManagerMutation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const managerData = managerQuery.data?.data;
  const locationData = locationQuery.data?.data;
  const massagers: ClinicLocationMassagerItem[] = locationData?.massagers ?? [];

  const handleUpdate = async (payload: ClinicLocationManagerUpdatePayload) => {
    if (locationId == null || managerIdNum == null) return;
    try {
      await updateMutation.mutateAsync({
        siteType,
        clinicLocationId: locationId,
        managerId: managerIdNum,
        payload,
      });
    } catch (err) {
      if (isNotFoundError(err)) {
        navigate(`/${siteType}/admin/404`, { replace: true });
      }
      throw err;
    }
  };

  const handleDelete = async () => {
    if (locationId == null || managerIdNum == null) return;
    try {
      await deleteMutation.mutateAsync({
        siteType,
        clinicLocationId: locationId,
        managerId: managerIdNum,
      });
    } catch (err) {
      if (isNotFoundError(err)) {
        navigate(`/${siteType}/admin/404`, { replace: true });
        return;
      }
      throw err;
    }
    navigate(`/${siteType}/admin/clinic_locations/${locationId}`);
  };

  if (managerQuery.isLoading || locationQuery.isLoading) {
    return (
      <div className="flex min-h-[200px] items-center justify-center p-6">
        <Spin size="large" />
      </div>
    );
  }

  const invalidParams =
    locationId == null ||
    managerIdNum == null ||
    Number.isNaN(managerIdNum) ||
    managerIdNum <= 0;

  if (invalidParams) {
    return <SiteAdminNotFoundTemplate />;
  }

  if (
    (locationQuery.isError && isNotFoundError(locationQuery.error)) ||
    (managerQuery.isError && isNotFoundError(managerQuery.error))
  ) {
    return <SiteAdminNotFoundTemplate />;
  }

  if (managerQuery.isError || locationQuery.isError) {
    return (
      <div className="p-6 text-red-600">
        {managerQuery.error?.message ??
          locationQuery.error?.message ??
          'データの取得に失敗しました。'}
      </div>
    );
  }

  if (!managerData || !locationData) {
    return <SiteAdminNotFoundTemplate />;
  }

  if (locationData.is_parent_clinic_deleted) {
    return <SiteAdminNotFoundTemplate />;
  }

  const clinicId = managerData.clinic_id;
  const kyotenBreadcrumbSuffix = massagerSerialForManagerEditBreadcrumb(
    massagers,
    managerData.massager_id,
    managerData.massager_serial_number,
    locationId!
  );

  return (
    <CreateClinicLocationManagerForm
      locationId={locationId!}
      clinicId={clinicId}
      kyotenBreadcrumbSuffix={kyotenBreadcrumbSuffix}
      locationName={locationData.location_name ?? ''}
      locationNameKana={locationData.location_name_kana ?? undefined}
      availableFrom={locationData.available_from ?? ''}
      availableTo={locationData.available_to ?? null}
      massagers={massagers}
      locationService={locationData.service ?? null}
      siteType={siteType}
      navigate={(path, options) => navigate(path, options)}
      editMode
      initialData={managerData}
      onUpdate={handleUpdate}
      refetchClinicLocation={() => locationQuery.refetch()}
      refetchManager={() => managerQuery.refetch()}
      isUpdatePending={updateMutation.isPending}
      onDelete={handleDelete}
      isDeletePending={deleteMutation.isPending}
      auditInfo={
        <AuditInfo
          createdBy={managerData?.created_by}
          updatedBy={managerData?.updated_by}
          updatedAt={managerData?.updated_at}
        />
      }
    />
  );
}
